import { MAX_SKILL_DESCRIPTION_LENGTH, MAX_SKILL_NAME_LENGTH, SkillValidationResult } from './SkillModel';

/**
 * Validate a skill name against the Agent Skills spec (mirrors deepagents' validateSkillName so a
 * name accepted here is accepted by the runtime loader): 1–64 chars, lowercase alphanumeric + hyphens,
 * no leading/trailing hyphen, no consecutive `--`. Shared by the guided builder (live feedback) and the
 * main-process SkillsService (enforcement).
 */
export function validateSkillName(name: string): SkillValidationResult {
    const value = (name ?? '').trim();
    if (!value) {
        return { valid: false, error: 'A name is required.' };
    }
    if (value.length > MAX_SKILL_NAME_LENGTH) {
        return { valid: false, error: `Name must be ${MAX_SKILL_NAME_LENGTH} characters or fewer.` };
    }
    // Unicode-lowercase alphanumeric + hyphen. Use the case-insensitive-free check: reject any uppercase
    // A–Z and any character that isn't a letter, digit, or hyphen.
    if (/[A-Z]/.test(value)) {
        return { valid: false, error: 'Use lowercase letters only.' };
    }
    if (!/^[\p{Ll}\p{Nd}-]+$/u.test(value)) {
        return { valid: false, error: 'Use lowercase letters, numbers, and hyphens only.' };
    }
    if (value.startsWith('-') || value.endsWith('-')) {
        return { valid: false, error: 'Name cannot start or end with a hyphen.' };
    }
    if (value.includes('--')) {
        return { valid: false, error: 'Name cannot contain consecutive hyphens.' };
    }
    return { valid: true };
}

/** Validate a skill description: non-empty and within the spec's length cap. */
export function validateSkillDescription(description: string): SkillValidationResult {
    const value = (description ?? '').trim();
    if (!value) {
        return { valid: false, error: 'A description is required so the assistant knows when to use this skill.' };
    }
    if (value.length > MAX_SKILL_DESCRIPTION_LENGTH) {
        return { valid: false, error: `Description must be ${MAX_SKILL_DESCRIPTION_LENGTH} characters or fewer.` };
    }
    return { valid: true };
}

/**
 * Derive a spec-valid skill name from a free-text title (for the builder's "auto" name): lowercase,
 * spaces/underscores → hyphens, strip disallowed chars, collapse repeats, trim hyphens, cap length.
 * Returns '' when nothing usable remains (caller then asks the user to enter a name).
 */
export function slugifySkillName(input: string): string {
    const slug = (input ?? '')
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
        .replace(/[^\p{Ll}\p{Nd}-]/gu, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_SKILL_NAME_LENGTH)
        .replace(/-+$/g, '');
    return slug;
}
